import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useQuery } from '../../../api/graphql'
import { UNREAD_COUNT_QUERY } from './chatQueries'

type ChatContextType = {
  isOpen: boolean
  activeConversationId: string | null
  unreadCount: number
  openChat: () => void
  closeChat: () => void
  openConversation: (id: string) => void
  goToList: () => void
  startNewConversation: () => void
  showNewConversation: boolean
  refetchUnread: () => void
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const ChatContext = createContext<ChatContextType>({
  isOpen: false,
  activeConversationId: null,
  unreadCount: 0,
  openChat: noop,
  closeChat: noop,
  openConversation: noop,
  goToList: noop,
  startNewConversation: noop,
  showNewConversation: false,
  refetchUnread: noop,
})

export const useChatContext = (): ChatContextType => useContext(ChatContext)

const ChatProvider: React.FC = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [showNewConversation, setShowNewConversation] = useState(false)

  const { data, refetch: refetchUnread } = useQuery(UNREAD_COUNT_QUERY, {
    pollInterval: 60000,
  })

  const unreadCount = data?.unreadConversationCount ?? 0

  // Auto-open chat panel when ?open_chat=1 is in the URL (e.g., from email CTA)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('open_chat') === '1') {
      setIsOpen(true)
      params.delete('open_chat')
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname
      window.history.replaceState({}, '', newUrl)
    }
  }, [])

  const openChat = useCallback(() => setIsOpen(true), [])
  const closeChat = useCallback(() => {
    setIsOpen(false)
    setActiveConversationId(null)
    setShowNewConversation(false)
  }, [])

  const openConversation = useCallback((id: string) => {
    setActiveConversationId(id)
    setShowNewConversation(false)
  }, [])

  const goToList = useCallback(() => {
    setActiveConversationId(null)
    setShowNewConversation(false)
  }, [])

  const startNewConversation = useCallback(() => {
    setShowNewConversation(true)
    setActiveConversationId(null)
  }, [])

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        activeConversationId,
        unreadCount,
        openChat,
        closeChat,
        openConversation,
        goToList,
        startNewConversation,
        showNewConversation,
        refetchUnread,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export default ChatProvider
